import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { EsignDisclosureOptInArgs } from 'signer-app/context/signer-app-client/signer-api';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { signerExperienceQueryKey } from './types';

type UseEsignDisclosureOptInParams = {
  signatureRequestId: string;
  accessCode: string | null;
};

export function useEsignDisclosureOptIn({
  signatureRequestId,
  accessCode,
}: UseEsignDisclosureOptInParams) {
  const client = useSignerAppClient();
  const queryClient = useQueryClient();

  const queryKey = signerExperienceQueryKey(signatureRequestId, accessCode);

  const { mutate: esignDisclosureOptIn } = useMutation({
    useErrorBoundary: true,
    mutationKey: [...queryKey, 'esign-opt-in'],
    mutationFn: async (args: EsignDisclosureOptInArgs) => {
      await client.signerApi.esignDisclosureOptIn(args);
    },
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);
      const prevResponse =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      queryClient.setQueryData<SignatureRequestResponse>(queryKey, (prev) => {
        if (prev) {
          return {
            ...prev,
            esignDisclosure: prev.esignDisclosure
              ? {
                  ...prev.esignDisclosure,
                  userOptIn: true,
                }
              : undefined,
          };
        }
        return undefined;
      });

      return { prevResponse };
    },
    onError: (_err, _args, context) => {
      queryClient.setQueryData(queryKey, context?.prevResponse);
    },
  });

  return { esignDisclosureOptIn };
}
